/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Tag,
  Typography,
  Popconfirm,
  message,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import { PlusOutlined, DownOutlined } from "@ant-design/icons";
import lodash from "lodash";
import { LayoutContent, DrawerHomeConfiguration } from "../../../components";
import { Images } from "../../../common/images";
import * as Styles from "./styles";
import {
  typeColor,
  configurationTypeLabel,
  childAddOptions,
} from "../../../usecases/HomeConfig/constant";
import {
  ConfigOption,
  ConfigTreeNode,
  ConfigurationType,
  HomeConfigFormData,
} from "../../../usecases/HomeConfig/types";
import { HomeConfigUsecase } from "../../../usecases/HomeConfig/usecase";
import { HomeRepository } from "../../../services/Home/home.repository";
import { LocalStorage } from "../../../modules/LocalStorage";
import { GetHomeDto } from "../../../services/Home/dto/get.dto";
import { MaterialRepository } from "../../../services/Material/material.repository";
import { GetMaterialDto } from "../../../services/Material/dto/get.dto";
import { ProductUsecase } from "../../../usecases/Product/usecase";
import { GetProductDto } from "../../../services/Product/dto/get.dto";
import { useProductDispatch } from "../../../usecases/Product";
import { useSettingState } from "../../../usecases/Setting";

const HomeConfigurationComponent: React.FC = () => {
  const [tree, setTree] = React.useState<ConfigTreeNode[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [keyword, setKeyword] = React.useState<string>("");

  const settingState = useSettingState();

  // FIXME: consider using a single useEffect with Promise.all to load homes, materials, and products in parallel instead of sequentially.
  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const items = await new HomeConfigUsecase().getConfigTree();
        setTree(items);
      } catch (error) {
        message.error("Failed to load home configurations");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const [homes, setHomes] = React.useState<GetHomeDto[]>([]);

  React.useEffect(() => {
    const loadHomes = async () => {
      try {
        const tenantId = LocalStorage.getTenantInfo()?.tenantId;
        const items = await new HomeRepository().getList({
          tenant_id: tenantId,
        });
        setHomes(items);
      } catch (error) {
        message.error("Failed to load homes");
      }
    };
    loadHomes();
  }, []);

  const [materials, setMaterials] = React.useState<GetMaterialDto[]>([]);

  React.useEffect(() => {
    const loadMaterials = async () => {
      try {
        const tenantId = LocalStorage.getTenantInfo()?.tenantId;
        const items = await new MaterialRepository().getList({ tenantId });
        setMaterials(items);
      } catch (error) {
        message.error("Failed to load materials");
      }
    };
    loadMaterials();
  }, []);

  const productDispatch = useProductDispatch();
  const [products, setProducts] = React.useState<GetProductDto[]>([]);

  React.useEffect(() => {
    const loadProducts = async () => {
      try {
        const items = await new ProductUsecase(productDispatch).getList({
          tenantId: settingState.tenantInfo?.tenantId,
        });
        setProducts(items);
      } catch (error) {
        message.error("Failed to load products");
      }
    };
    loadProducts();
  }, [productDispatch, settingState.tenantInfo?.tenantId]);

  const [drawerVisible, setDrawerVisible] = React.useState<boolean>(false);
  const [drawerVariant, setDrawerVariant] = React.useState<
    "options" | "baseOption"
  >("options");
  const [editingConfig, setEditingConfig] = React.useState<
    HomeConfigFormData | undefined
  >();
  const [drawerDefaults, setDrawerDefaults] = React.useState<{
    type: ConfigurationType;
    homeId?: string;
    region?: string;
    community?: string;
    userId?: string;
    options?: ConfigOption[];
  }>({ type: "base" });

  const takenHomeIds = tree.map((node) => node.config.homeId);

  const filteredTree = !keyword.trim()
    ? tree
    : tree.filter((node) => {
        return node.config.homeName
          .toLowerCase()
          .includes(keyword.toLowerCase());
      });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChange = React.useCallback(
    lodash.debounce((event) => {
      setKeyword(event.target.value);
    }, 300),
    [],
  );

  const reloadTree = async () => {
    const items = await new HomeConfigUsecase().getConfigTree();
    setTree(items);
  };

  const onOpenCreate = () => {
    setEditingConfig(undefined);
    setDrawerDefaults({ type: "base" });
    setDrawerVisible(true);
  };

  const onOpenCreateChild = async (
    parent: ConfigTreeNode,
    type: ConfigurationType,
  ) => {
    setEditingConfig(undefined);
    setLoading(true);
    try {
      const options = await new HomeConfigUsecase().fetchConfigJson({
        configLink: parent.config.configLink,
      });
      setDrawerDefaults({
        type,
        homeId: parent.config.homeId,
        region: parent.config.region ?? undefined,
        community: parent.config.community ?? undefined,
        userId: parent.config.userId ?? undefined,
        options,
      });
      setDrawerVisible(true);
    } catch (error) {
      message.error("Failed to load parent configuration");
    } finally {
      setLoading(false);
    }
  };

  const openEditDrawer = async (
    node: ConfigTreeNode,
    variant: "options" | "baseOption",
  ) => {
    setLoading(true);
    try {
      const options = await new HomeConfigUsecase().fetchConfigJson({
        configLink: node.config.configLink,
      });
      setEditingConfig({
        id: node.key,
        homeId: node.config.homeId,
        homeName: node.config.homeName,
        type: node.type,
        scopeName: node.displayName,
        region: node.config.region ?? undefined,
        community: node.config.community ?? undefined,
        userId: node.config.userId ?? undefined,
        options,
      });
      setDrawerVariant(variant);
      setDrawerVisible(true);
    } catch (error) {
      message.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const onDrawerClose = () => {
    setDrawerVisible(false);
    setDrawerVariant("options");
    setEditingConfig(undefined);
  };

  const onSaveConfig = async (config: HomeConfigFormData) => {
    const tenantId = LocalStorage.getTenantInfo()?.tenantId;
    if (!tenantId) {
      message.error("Missing tenant information");
      return;
    }

    const scope = editingConfig
      ? {
          region: editingConfig.region,
          community: editingConfig.community,
          userId: editingConfig.userId,
        }
      : {
          region:
            config.type === "region" ? config.scopeName : drawerDefaults.region,
          community:
            config.type === "community"
              ? config.scopeName
              : drawerDefaults.community,
          userId: config.type === "user" ? config.scopeName : undefined,
        };

    setLoading(true);
    try {
      if (editingConfig) {
        await new HomeConfigUsecase().updateConfig({
          homeId: config.homeId,
          tenantId,
          configuration: config.options,
          ...scope,
        });
        message.success("Updated configuration successfully");
      } else {
        await new HomeConfigUsecase().createConfig({
          homeId: config.homeId,
          homeName: config.homeName,
          tenantId,
          configuration: config.options,
          ...scope,
        });
        message.success("Created configuration successfully");
      }
      setDrawerVisible(false);
      await reloadTree();
    } catch (error) {
      if (error?.code === "DUPLICATED_ITEM") {
        message.error(
          "This home already has a configuration, please choose a different one.",
          5,
        );
        reloadTree();
      } else {
        message.error(error?.message ?? "Failed to save home configuration");
      }
    } finally {
      setLoading(false);
    }
  };

  const onDeleteConfig = async (node: ConfigTreeNode) => {
    setLoading(true);
    try {
      await new HomeConfigUsecase().deleteConfig({
        homeId: node.config.homeId,
        region: node.config.region ?? undefined,
        community: node.config.community ?? undefined,
        userId: node.config.userId ?? undefined,
      });
      message.success("Deleted configuration successfully");
      await reloadTree();
    } catch (error) {
      message.error("Failed to delete home configuration");
    } finally {
      setLoading(false);
    }
  };

  const renderConfigsColumnCell = (_: unknown, record: ConfigTreeNode) => {
    const { configLink } = record.config;
    if (!configLink) {
      return <Typography.Text type="secondary">—</Typography.Text>;
    }
    return (
      <Typography.Link
        href={configLink}
        target="_blank"
        rel="noreferrer"
        ellipsis
        css={Styles.configCellStyle}
      >
        Read config
      </Typography.Link>
    );
  };

  const renderTypeCell = (_: unknown, record: ConfigTreeNode) => {
    return (
      <Tag color={typeColor[record.type]}>
        {configurationTypeLabel[record.type]}
      </Tag>
    );
  };

  const renderActionsCell = (_: unknown, record: ConfigTreeNode) => {
    const handleOpenEdit = () => {
      openEditDrawer(record, "options");
    };
    const handleOpenEditBaseOption = () => {
      openEditDrawer(record, "baseOption");
    };
    const menu = (
      <Menu>
        {childAddOptions[record.type].map((option) => {
          if (option.action === "editBaseOption") {
            return (
              <Menu.Item
                key="edit-base-option"
                onClick={handleOpenEditBaseOption}
              >
                {option.label}
              </Menu.Item>
            );
          }
          if (option.action === "editOptions") {
            return (
              <Menu.Item key="edit" onClick={handleOpenEdit}>
                {option.label}
              </Menu.Item>
            );
          }
          if (option.action === "delete") {
            const hasChildren = Boolean(record.children?.length);
            return (
              <Menu.Item key="delete" danger disabled={hasChildren}>
                {hasChildren ? (
                  <Tooltip title="Cannot delete config that have region/community/user config">
                    {option.label}
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title="Delete this config?"
                    onConfirm={() => onDeleteConfig(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <div css={Styles.fullWidthItemStyle}>{option.label}</div>
                  </Popconfirm>
                )}
              </Menu.Item>
            );
          }
          return (
            <Menu.Item
              key={option.type}
              onClick={() => {
                onOpenCreateChild(record, option.type);
              }}
            >
              {option.label}
            </Menu.Item>
          );
        })}
      </Menu>
    );

    return (
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button type="link" size="small">
          Actions <DownOutlined css={Styles.actionIconStyle} />
        </Button>
      </Dropdown>
    );
  };

  // One name per row: home name for base, scope name (region / community / user
  // id) for nested rows — indented by depth so the hierarchy reads clearly.
  const renderNameCell = (_: unknown, record: ConfigTreeNode) => {
    const label =
      record.type === "base" ? record.config.homeName : record.displayName;
    return (
      <span css={[Styles.nameCellStyle, Styles.nameIndentStyle(record.depth)]}>
        {record.depth > 0 ? (
          <span css={Styles.treeConnectorStyle}>└─</span>
        ) : null}
        {label}
      </span>
    );
  };

  return (
    <LayoutContent
      title="HOME CONFIGURATION"
      iconPath={Images.SELECTION_DE}
      toolbar={
        <Space size="large">
          <Input.Search
            placeholder="Find..."
            allowClear
            css={Styles.searchInputStyle}
            size="large"
            onChange={onKeywordChange}
          />
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={onOpenCreate}
          >
            Create
          </Button>
        </Space>
      }
    >
      <div css={[Styles.scrollAreaStyle, Styles.tableHeaderStyle]}>
        <Table<ConfigTreeNode>
          css={Styles.tableStyle}
          tableLayout="fixed"
          rowKey="key"
          loading={loading}
          dataSource={filteredTree}
          pagination={false}
          expandable={{
            expandIconColumnIndex: 0,
            indentSize: 0,
            rowExpandable: (record) => {
              return Boolean(record.children?.length);
            },
          }}
          columns={[
            {
              title: "",
              key: "expand",
              width: 56,
            },
            {
              title: "Name",
              key: "name",
              width: 300,
              render: renderNameCell,
            },
            {
              title: "Type",
              key: "type",
              width: 120,
              render: renderTypeCell,
            },
            {
              title: "Configs",
              key: "configs",
              width: 120,
              render: renderConfigsColumnCell,
            },
            {
              title: "Actions",
              key: "actions",
              width: 160,
              render: renderActionsCell,
            },
          ]}
        />
      </div>

      <DrawerHomeConfiguration
        visible={drawerVisible}
        onClose={onDrawerClose}
        onSave={onSaveConfig}
        loading={loading}
        homeDataList={homes}
        takenHomeIds={takenHomeIds}
        materials={materials}
        products={products}
        editingConfig={editingConfig}
        defaultType={drawerDefaults.type}
        defaultHomeId={drawerDefaults.homeId}
        defaultOptions={drawerDefaults.options}
        variant={drawerVariant}
      />
    </LayoutContent>
  );
};

export const HomeConfiguration = React.memo(HomeConfigurationComponent);
